import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { HoverHintDefinitionGrid } from "@/components/ui/HoverHintDefinitionGrid";
import { apiFetch } from "@/lib/api";

export interface EndUserPeekRow {
  fs_account_id: number;
  fs_account_name: string;
  mv_end_user_id: number | null;
  mv_end_user_name: string | null;
  mv_client_name: string | null;
  rep_agency: string | null;
  stocking: string | null;
}

function detailField(value: ReactNode): ReactNode {
  if (value == null) {
    return "—";
  }
  if (typeof value === "string" && value.trim() === "") {
    return "—";
  }
  return value;
}

function EndUserPeekBody({ row }: { row: EndUserPeekRow }): JSX.Element {
  return (
    <HoverHintDefinitionGrid
      rows={[
        { label: "Account", value: detailField(row.fs_account_name) },
        { label: "Megaventory end user", value: detailField(row.mv_end_user_name) },
        { label: "Megaventory client", value: detailField(row.mv_client_name) },
        { label: "Rep agency", value: detailField(row.rep_agency) },
        { label: "Stocking", value: detailField(row.stocking) },
      ]}
    />
  );
}

export function EndUserDetailHoverHint({ fsAccountId }: { fsAccountId: number }): JSX.Element {
  const detailQuery = useQuery({
    queryKey: ["inside-sales", "customer", fsAccountId],
    queryFn: () => apiFetch<{ row: EndUserPeekRow }>(`/api/inside-sales/customers/${String(fsAccountId)}`),
    staleTime: 60_000,
  });

  if (detailQuery.isLoading) {
    return (
      <p className="flex items-center gap-1.5 text-xs text-slate-600">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
        Loading customer…
      </p>
    );
  }

  if (detailQuery.isError || detailQuery.data?.row == null) {
    return <p className="text-xs text-slate-600">Could not load customer preview.</p>;
  }

  return <EndUserPeekBody row={detailQuery.data.row} />;
}

export function buildInsideSalesCustomerPath(fsAccountId: number): string {
  return `/inside-sales/customers?fs_account_id=${String(fsAccountId)}`;
}
